import { Alert } from '@creditornot/cb-alert';
import { IconButton } from '@creditornot/cb-button';
import { Minus } from '@creditornot/cb-icons';
import { cssThemedValue } from '@creditornot/cb-ingredients';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import MonetaryAmount from 'common/formatting/MonetaryAmount';
import { MOBILE_BREAKPOINT } from 'common/hooks/useIsMobile';
import { Typography } from 'common/typography';
import Loading from 'core/Loading';
import { useDiscountedItem } from 'modules/campaigns/hooks/useDiscountedItem';
import { TestIds } from 'modules/campaigns/utils/testUtils';

type DiscountedItemDetailsProps = {
  itemId: string;
  menuId: string;
  count?: number;
  onRemove?: () => void;
};

const StyledDiscountedItemDetailsContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: flex-start;
  gap: 24px;
  margin-block-start: 8px;
`;

const StyledDiscountedItemImage = styled.img`
  border-radius: 8px;
  border: 1px solid ${cssThemedValue('borderPrimary')};
  width: 120px;
  height: 80px;
  object-fit: cover;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    width: 70px;
    height: 50px;
  }
`;

const StyledDiscountedItemDetails = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  align-self: stretch;
  flex: 1;
`;

const StyledDiscountedItemTitle = styled(Typography.Title2)`
  font-weight: 700;
`;

const StyledDiscountedItemDescription = styled(Typography.Body3)`
  color: ${cssThemedValue('textSecondary')};
`;

const StyledAlert = styled(Alert)`
  margin-block-start: 32px;
  margin-inline: 16px;
`;

const StyledLoadingContainer = styled.div`
  flex: 1 1;
  display: flex;
  justify-content: center;
`;

export const DiscountedItemDetails = ({ itemId, menuId, onRemove }: DiscountedItemDetailsProps) => {
  const { discountedItem, isFetching } = useDiscountedItem({ itemId, menuId });

  return (
    <StyledDiscountedItemDetailsContainer>
      {isFetching ? (
        <StyledLoadingContainer>
          <Loading />
        </StyledLoadingContainer>
      ) : discountedItem ? (
        <>
          {discountedItem.image ? <StyledDiscountedItemImage src={discountedItem.image} /> : null}
          <StyledDiscountedItemDetails>
            <StyledDiscountedItemTitle>{discountedItem.name}</StyledDiscountedItemTitle>
            <StyledDiscountedItemDescription>{discountedItem.description}</StyledDiscountedItemDescription>
            <Typography.Body3 fontWeight="semiBold">
              <MonetaryAmount
                amount={parseFloat(discountedItem.baseprice.amount)}
                currency={discountedItem.baseprice.currency}
              />
            </Typography.Body3>
          </StyledDiscountedItemDetails>
          {onRemove && (
            <IconButton
              size="tiny"
              variant="red"
              onClick={() => onRemove()}
              data-testid={TestIds.FORM.DISCOUNTED_ITEM.REMOVE}
            >
              <Minus />
            </IconButton>
          )}
        </>
      ) : (
        <StyledAlert variant="error">
          <FormattedMessage id="campaigns.menu-unavailable" />
        </StyledAlert>
      )}
    </StyledDiscountedItemDetailsContainer>
  );
};
